import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  ChargeArtisanAllResponse,
  ChargeArtisanCell,
  ChargeArtisanRow,
  ChargeArtisanSection,
  SectionKey,
} from "types/planning";
import { planningsApi } from "@services/api/plannings";
import { searchPartnersFull } from "@services/algolia";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import SmallSpinner from "@components/ui/loading/SmallSpinner";
import Icon1 from "@assets/icons/Icon";
import { showNotification } from "context/notification.service";

interface Props {
  orgId: string;
  initialNameFilter?: string;
}

type MergedRow = {
  artisan_id: string;
  artisan_name: string;
  cells: Record<string, ChargeArtisanCell[]>;
};

/** Parse "YYYY-WW" → {year, week}. Returns null on malformed input. */
function parseWeekKey(key: string): { year: number; week: number } | null {
  const [y, w] = key.split("-", 2);
  const year = Number(y);
  const week = Number(w);
  if (!Number.isFinite(year) || !Number.isFinite(week)) return null;
  return { year, week };
}

/** ISO year and week number for today, used to clip past weeks out of the grid. */
function todayIsoYearWeek(): { year: number; week: number } {
  const now = new Date();
  const utc = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
  );
  // Thursday of the current week decides the ISO year.
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((+utc - +yearStart) / 86_400_000) + 1) / 7);
  return { year: utc.getUTCFullYear(), week };
}

/**
 * Merge every section's artisan rows into a single flat list. When the same
 * artisan appears in multiple sections, their cells are unioned per week so
 * the cross-section view shows the full workload at a glance.
 */
function mergeSections(sections: ChargeArtisanSection[]): MergedRow[] {
  const byId = new Map<string, MergedRow>();
  for (const section of sections) {
    for (const row of section.artisan_rows) {
      const existing = byId.get(row.artisan_id);
      if (!existing) {
        byId.set(row.artisan_id, {
          artisan_id: row.artisan_id,
          artisan_name: row.artisan_name,
          cells: { ...row.cells },
        });
        continue;
      }
      for (const [weekKey, cells] of Object.entries(row.cells)) {
        const merged = existing.cells[weekKey] ? [...existing.cells[weekKey]] : [];
        for (const cell of cells) {
          if (!merged.some((c) => c.project_id === cell.project_id)) {
            merged.push(cell);
          }
        }
        existing.cells[weekKey] = merged;
      }
    }
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.artisan_name.localeCompare(b.artisan_name, "fr"),
  );
}

export const ChargeArtisanTable = ({ orgId, initialNameFilter = "" }: Props) => {
  const [data, setData] = useState<ChargeArtisanAllResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [sectionKey, setSectionKey] = useState<SectionKey | null>(null);
  const [nameFilter, setNameFilter] = useState(initialNameFilter);
  // Extra rows for partners whose name matches the search but who have no
  // charge in any planning — populated from Algolia so users discover that
  // the artisan exists (they just have an empty row).
  const [algoliaExtraRows, setAlgoliaExtraRows] = useState<MergedRow[]>([]);
  const [popup, setPopup] = useState<{
    cells: ChargeArtisanCell[];
    x: number;
    y: number;
  } | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Keep the input in sync when the parent swaps initialNameFilter
  // (e.g. user clicks the Voir charge icon on a different artisan).
  useEffect(() => {
    setNameFilter(initialNameFilter);
  }, [initialNameFilter]);

  const fetchAll = useCallback(
    async (force: boolean = false) => {
      if (force) setRecomputing(true);
      else setLoading(true);
      try {
        const result = await planningsApi.getChargeArtisanAll({
          org_id: orgId,
          force,
        });
        setData(result);
      } catch {
        setData(null);
        showNotification({
          type: "error",
          message: "Impossible de charger la charge artisans",
        });
      } finally {
        setLoading(false);
        setRecomputing(false);
      }
    },
    [orgId],
  );

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  // Close popup on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopup(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Algolia-backed fallback: when the user searches for a name, look up
  // matching partners in Algolia and add empty rows for any that aren't
  // already represented in the charge data. Debounced to avoid hammering
  // Algolia while the user is still typing.
  useEffect(() => {
    const q = nameFilter.trim();
    if (!q) {
      setAlgoliaExtraRows([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const hits = await searchPartnersFull(q, orgId);
        if (cancelled) return;
        // IDs already surfaced by the charge data (across all sections).
        const existingIds = new Set<string>();
        if (data) {
          for (const section of data.sections) {
            for (const row of section.artisan_rows) existingIds.add(row.artisan_id);
          }
        }
        const extras: MergedRow[] = hits
          .filter((h) => !existingIds.has(h.value))
          .map((h) => ({
            artisan_id: h.value,
            artisan_name: h.label || h.extra_label_2 || "",
            cells: {},
          }))
          .filter((r) => r.artisan_name); // skip unnamed partners
        setAlgoliaExtraRows(extras);
      } catch {
        if (!cancelled) setAlgoliaExtraRows([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [nameFilter, orgId, data]);

  const handleForceRecompute = async () => {
    await fetchAll(true);
    showNotification({ type: "success", message: "Charge recalculée" });
  };

  function handleCellClick(cells: ChargeArtisanCell[], e: React.MouseEvent) {
    setPopup({ cells, x: e.clientX, y: e.clientY });
  }

  // Rows that drive the current grid — either one section's rows or every
  // section merged together, then filtered by the name input. When the user
  // types a name, we also append zero-charge matches from Algolia so
  // partners without any planning activity are still discoverable.
  const visibleRows: (ChargeArtisanRow | MergedRow)[] = useMemo(() => {
    if (!data) return [];
    const base: (ChargeArtisanRow | MergedRow)[] = sectionKey
      ? data.sections.find((s) => s.section_key === sectionKey)?.artisan_rows ?? []
      : mergeSections(data.sections);
    const q = nameFilter.trim().toLowerCase();
    if (!q) return base;
    const localMatches = base.filter((row) =>
      row.artisan_name.toLowerCase().includes(q),
    );
    const localIds = new Set(localMatches.map((r) => r.artisan_id));
    const extras = algoliaExtraRows.filter((r) => !localIds.has(r.artisan_id));
    return [...localMatches, ...extras].sort((a, b) =>
      a.artisan_name.localeCompare(b.artisan_name, "fr"),
    );
  }, [data, sectionKey, nameFilter, algoliaExtraRows]);

  // Clip the server's week list to "today and onward" — the backend
  // returns current + next year as a static window, so the first columns
  // are always past weeks the user doesn't need to see.
  const isoWeeks = useMemo(() => {
    const all = data?.iso_weeks ?? [];
    const today = todayIsoYearWeek();
    return all.filter((key) => {
      const p = parseWeekKey(key);
      if (!p) return true;
      if (p.year !== today.year) return p.year > today.year;
      return p.week >= today.week;
    });
  }, [data?.iso_weeks]);

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* Section selector */}
      <div className="flex flex-wrap items-center gap-2 min-h-32 flex-shrink-0">
        {loading && (
          <div className="flex items-center gap-2 text-h3 text-neutral-500">
            <SmallSpinner loading={true} />
            <span>Chargement de la charge artisans…</span>
          </div>
        )}
        {!loading &&
          data?.sections.map((s) => (
            <button
              key={s.section_key}
              onClick={() =>
                setSectionKey(sectionKey === s.section_key ? null : s.section_key)
              }
              className={`px-3 py-1 rounded-full text-h3 border transition-all ${
                sectionKey === s.section_key
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
              }`}
            >
              {s.label}
            </button>
          ))}
      </div>

      {/* Name filter + force recompute */}
      {!loading && data && (
        <div className="flex flex-row items-center gap-3 flex-wrap flex-shrink-0">
          <input
            type="text"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Filtrer par nom d'artisan..."
            className="border border-neutral-200 rounded-lg px-3 py-1.5 text-h3 outline-none focus:ring-2 focus:ring-neutral-300 max-w-300"
          />
          <SecondaryButton
            variant="small"
            startIcon={
              recomputing ? (
                <SmallSpinner loading={true} />
              ) : (
                <Icon1 name="sync" size={14} />
              )
            }
            onClick={handleForceRecompute}
            disabled={recomputing}
          >
            {recomputing ? "Calcul en cours..." : "Forcer un nouveau calcul"}
          </SecondaryButton>
          {!sectionKey && (
            <span className="text-h4 text-neutral-400">
              Vue toutes sections · sélectionnez un corps de métier pour filtrer
            </span>
          )}
        </div>
      )}

      {!loading && data && (
        <div className="flex-1 min-h-0 overflow-auto max-w-full rounded-xl border border-neutral-200">
          <table className="border-collapse min-w-max">
            <thead>
              <tr>
                <th
                  className="bg-neutral-100 border border-neutral-200 px-3 py-2 text-h4 text-left sticky left-0 z-10"
                  style={{ minWidth: 180 }}
                >
                  Artisan
                </th>
                {isoWeeks.map((key, idx) => {
                  const parsed = parseWeekKey(key);
                  const prevParsed = idx > 0 ? parseWeekKey(isoWeeks[idx - 1]) : null;
                  const isYearBoundary =
                    !!parsed && (!prevParsed || prevParsed.year !== parsed.year);
                  return (
                    <th
                      key={key}
                      className={`bg-neutral-100 px-1 py-2 text-h4 text-center font-medium border border-neutral-200 ${
                        isYearBoundary ? "border-l-2 border-l-neutral-400" : ""
                      }`}
                      style={{ minWidth: 32 }}
                    >
                      <div className="flex flex-col items-center leading-tight">
                        {isYearBoundary && parsed && (
                          <span className="text-h5 text-neutral-500 font-semibold">
                            {parsed.year}
                          </span>
                        )}
                        <span>S{parsed?.week ?? ""}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-4 text-h3 text-neutral-400"
                    colSpan={isoWeeks.length + 1}
                  >
                    {nameFilter.trim()
                      ? "Aucun artisan trouvé."
                      : sectionKey
                      ? "Aucune charge pour ce corps de métier."
                      : "Aucune charge enregistrée."}
                  </td>
                </tr>
              )}
              {visibleRows.map((row) => (
                <tr key={row.artisan_id}>
                  <td
                    className="bg-white border border-neutral-100 px-3 py-2 text-h3 font-medium sticky left-0 z-10"
                    style={{ minWidth: 180 }}
                  >
                    {row.artisan_name}
                  </td>
                  {isoWeeks.map((key, idx) => {
                    const parsed = parseWeekKey(key);
                    const prevParsed = idx > 0 ? parseWeekKey(isoWeeks[idx - 1]) : null;
                    const isYearBoundary =
                      !!parsed && (!prevParsed || prevParsed.year !== parsed.year);
                    const cells = row.cells[key] || [];
                    const count = cells.length;
                    return (
                      <td
                        key={key}
                        className={`text-center text-h4 font-bold transition-colors border border-neutral-100 ${
                          isYearBoundary ? "border-l-2 border-l-neutral-400" : ""
                        } ${
                          count >= 2
                            ? "bg-red-400 text-white cursor-pointer hover:bg-red-500"
                            : count === 1
                            ? "bg-green-500 text-white cursor-pointer hover:bg-green-600"
                            : "bg-white"
                        }`}
                        onClick={
                          count > 0 ? (e) => handleCellClick(cells, e) : undefined
                        }
                        style={{ minWidth: 32, height: 32 }}
                      >
                        {count >= 1 ? count : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && data.sections.length === 0 && (
        <div className="text-h3 text-neutral-400">
          Aucun planning actif. Créez un planning sur un projet pour voir la charge.
        </div>
      )}

      {/* Popup */}
      {popup && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-white border border-neutral-200 rounded-xl shadow-lg p-4 max-w-xs"
          style={{ top: popup.y + 8, left: popup.x + 8 }}
        >
          <div className="text-h3 font-bold mb-2">Chantiers concernés</div>
          <div className="flex flex-col gap-2">
            {popup.cells.map((cell, i) => (
              <div key={i} className="flex flex-col text-h4">
                <span className="font-medium text-neutral-800">
                  {cell.project_name}
                </span>
                {cell.conductor_name && (
                  <span className="text-neutral-500">
                    CDT : {cell.conductor_name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
